import ApiPaginated, { toLocalPaginated } from "./paginated";
import Paginated from "@/pagination/paginated";
import axios, { AxiosInstance } from "axios";

type RequestMethod = "get" | "post";
class AsyncPaginatedRequestIterator<A> implements AsyncIterator<Paginated<A>> {
  public currentPage = 0;
  public lastPage: number | null = null;
  public endpoint: string;
  public params?: unknown;
  public body?: unknown;
  public method: RequestMethod;
  private _httpClient: AxiosInstance = axios;
  constructor(
    method: RequestMethod,
    endpoint: string,
    params?: unknown,
    body?: unknown
  ) {
    this.endpoint = endpoint;
    this.params = params;
    this.body = body;
    this.method = method;
  }
  public async next(): Promise<IteratorResult<Paginated<A>>> {
    this.currentPage = this.currentPage + 1;
    // this.params may have other parameters at runtime.
    // The type lowering is required to add de page value;
    let reqParams = { page: this.currentPage };
    if (this.params != undefined) {
      reqParams = { ...reqParams, ...(this.params as Record<string, unknown>) };
    }
    if (this.lastPage === null || this.currentPage <= this.lastPage) {
      const response = await this._httpClient.request<ApiPaginated<A>>({
        method: this.method,
        url: this.endpoint,
        data: this.body,
        params: reqParams,
      });
      const result = toLocalPaginated(response.data);
      this.lastPage = result.lastPage;
      const res: IteratorResult<Paginated<A>> = {
        done: false,
        value: result,
      };
      return res;
    }
    return { done: true, value: undefined };
  }

  public setClient(client: AxiosInstance) {
    this._httpClient = client;
    return this;
  }
}

export default class AsyncPaginatedRequest<T>
  implements AsyncIterable<Paginated<T>>
{
  private _method: RequestMethod;
  private _endpoint: string;
  private _params?: unknown;
  private _body?: unknown;
  private _httpClient: AxiosInstance;

  constructor(
    method: RequestMethod,
    endpoint: string,
    params?: unknown,
    body?: unknown,
    httpClient: AxiosInstance = axios
  ) {
    this._method = method;
    this._endpoint = endpoint;
    this._params = params;
    this._body = body;
    this._httpClient = httpClient;
  }

  [Symbol.asyncIterator](): AsyncIterator<Paginated<T>> {
    return new AsyncPaginatedRequestIterator<T>(
      this._method,
      this._endpoint,
      this._params,
      this._body
    ).setClient(this._httpClient);
  }

  asyncIter(): AsyncIterator<Paginated<T>> {
    return this[Symbol.asyncIterator]();
  }
}
